import { createTheme } from '@mui/material/styles';

// Estender a interface Palette do MUI para incluir a cor 'fox'
declare module '@mui/material/styles' {
  interface PaletteColor {
    gradient?: string;
    background?: string;
  }
  interface SimplePaletteColorOptions {
    gradient?: string;
    background?: string;
  }

  interface Palette {
    elite: PaletteColor;
  }
  interface PaletteOptions {
    elite?: SimplePaletteColorOptions;
  }
}

// Definir cores elite como constantes para reutilização
const eliteColors = {
  main: '#25a18e',
  light: '#79b51b',
  dark: '#649719',
  contrastText: '#ffffff',
  gradient: 'linear-gradient(135deg, #649719 0%, #25a18e 100%)',
  background: '#E8F4FF',
};

// Cores do tema
const colors = {
  elite: eliteColors,
  primary: {
    main: '#25a18e',
    light: '#42a5f5',
    dark: '#649719',
    contrastText: '#fff',
  },
  secondary: {
    main: '#dc004e',
    light: '#f73378',
    dark: '#9a0036',
    contrastText: '#fff',
  },
  success: {
    main: '#2e7d32',
    light: '#81c784',
    dark: '#388e3c',
  },
  error: {
    main: '#d32f2f',
    light: '#e57373',
    dark: '#c62828',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#f57c00',
  },
  info: {
    main: '#2196f3',
    light: '#64b5f6',
    dark: '#1976d2',
  },
  background: {
    default: '#f5f5f5',
    paper: '#ffffff',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
};

// Breakpoints
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
};

// Tipografia
const typography = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: '2.5rem',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.6,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.43,
  },
  button: {
    textTransform: 'none' as const,
    fontWeight: 500,
  },
};

// Espaçamento
const spacing = 8;

// Sombras customizadas
const shadows = [
  'none',
  '0px 2px 4px rgba(0,0,0,0.05)',
  '0px 4px 8px rgba(0,0,0,0.08)',
  '0px 8px 16px rgba(0,0,0,0.1)',
  '0px 12px 24px rgba(0,0,0,0.12)',
  '0px 16px 32px rgba(0,0,0,0.14)',
  '0px 20px 40px rgba(0,0,0,0.16)',
  '0px 24px 48px rgba(0,0,0,0.18)',
  '0px 28px 56px rgba(0,0,0,0.2)',
  '0px 32px 64px rgba(0,0,0,0.22)',
  '0px 2px 4px rgba(0,0,0,0.05)',
  '0px 4px 8px rgba(0,0,0,0.08)',
  '0px 8px 16px rgba(0,0,0,0.1)',
  '0px 12px 24px rgba(0,0,0,0.12)',
  '0px 16px 32px rgba(0,0,0,0.14)',
  '0px 20px 40px rgba(0,0,0,0.16)',
  '0px 24px 48px rgba(0,0,0,0.18)',
  '0px 28px 56px rgba(0,0,0,0.2)',
  '0px 32px 64px rgba(0,0,0,0.22)',
  '0px 36px 72px rgba(0,0,0,0.24)',
  '0px 40px 80px rgba(0,0,0,0.26)',
  '0px 44px 88px rgba(0,0,0,0.28)',
  '0px 48px 96px rgba(0,0,0,0.3)',
  '0px 52px 104px rgba(0,0,0,0.32)',
  '0px 56px 112px rgba(0,0,0,0.34)',
];

// Tema principal
const theme = createTheme({
  palette: colors,
  breakpoints,
  typography,
  spacing,
  shadows: shadows as any,
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
          },
        },
        sizeSmall: {
          padding: '4px 10px',
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '12px 24px',
          fontSize: '0.9375rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0px 4px 16px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
        },
        elevation2: {
          boxShadow: '0px 4px 8px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          boxShadow: '0px -2px 8px rgba(0,0,0,0.08)',
          borderTop: '1px solid rgba(0,0,0,0.08)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
  },
});

export default theme;